import { isDirectApiMode } from '../api-paths';
import { getDistrictId, getHighschoolId } from '../cp-context';

function getStudentHighschoolId(): string {
  const highschoolId = getHighschoolId();

  // Local staff dev commonly sets VITE_HIGHSCHOOL_ID=0 for district-wide views.
  // Student endpoints require a concrete school context.
  if (highschoolId && highschoolId !== '0') {
    return highschoolId;
  }

  return import.meta.env.VITE_STUDENT_HIGHSCHOOL_ID || '88881USPU';
}

/**
 * Student course-plans base path.
 * Embedded in FC/FCUI: `/course-planner-next/student-course-plans` (FC proxy).
 * Local dev (direct): uses district/highschool-scoped path via getCoursePlannerPath.
 */
export function getStudentCoursePlansBasePath(): string {
  if (isDirectApiMode()) {
    const districtId = getDistrictId();
    const highschoolId = getStudentHighschoolId();
    return `/student-course-plans/district/${encodeURIComponent(districtId)}/highschool/${encodeURIComponent(highschoolId)}`;
  }
  return '/course-planner-next/student-course-plans';
}

export function getStudentCoursePlansBasePathForHighschool(highschoolId: string): string {
  if (isDirectApiMode()) {
    const districtId = getDistrictId();
    return `/student-course-plans/district/${encodeURIComponent(districtId)}/highschool/${encodeURIComponent(highschoolId)}`;
  }
  return '/course-planner-next/student-course-plans';
}

export function getStudentCoursePlanDetailPath(planId: string): string {
  return `${getStudentCoursePlansBasePath()}/${planId}`;
}

export function getStudentCoursePlansByStudentPath(studentId: string): string {
  return `${getStudentCoursePlansBasePath()}/student/${encodeURIComponent(studentId)}`;
}

export function getStudentCoursePlansSetCurrentPath(): string {
  return `${getStudentCoursePlansBasePath()}/set-current`;
}

/**
 * Build the POST path for submitting a plan for approval.
 *
 * @returns URL path for the approval endpoint
 */
export function getStudentCoursePlansApprovalPath(): string {
  return `${getStudentCoursePlansBasePath()}/approval`;
}

/**
 * Build the GET path for triggering the parent-signed document generation.
 * Legacy CPUI calls GET /course-planner-next/student-course-plans/{id}/sign?approved={bool}&parentName={name}
 * after a successful parent approval. This generates the signed document saved to "About me > Documents shared with me".
 *
 * @param planId - The student course plan ID
 * @param approved - Whether the parent approved or rejected the plan
 * @param parentName - The parent's full name as entered in the approval form
 */
export function getStudentCoursePlanSignPath(
  planId: string,
  approved: boolean,
  parentName: string
): string {
  return `${getStudentCoursePlanDetailPath(planId)}/sign?approved=${approved}&parentName=${encodeURIComponent(parentName)}`;
}

/**
 * Build the PUT path for updating requirements on a student course plan.
 *
 * @param planId - The student course plan ID
 * @returns URL path for the requirements endpoint
 */
export function getStudentCoursePlanRequirementsPath(planId: string): string {
  return `${getStudentCoursePlanDetailPath(planId)}/requirements`;
}

/**
 * Build the PUT path for updating a single requirement on a student course plan.
 * Legacy CPUI IFAL target delete uses PUT `/student-course-plans/:planId/requirement`
 * with `{ requirement, creditRecovery }`.
 *
 * @param planId - The student course plan ID
 * @returns URL path for the requirement endpoint
 */
export function getStudentCoursePlanRequirementPath(planId: string): string {
  return `${getStudentCoursePlanDetailPath(planId)}/requirement`;
}

/**
 * DELETE a single course row from a student plan (legacy CPUI `removeCourse` / `removeCourses`).
 *
 * @param planId - Student course plan id
 * @param courseId - Plan or catalog course id
 * @param courseStatementUnique - Disambiguator when present on the row; omit as empty string otherwise
 */
export function getStudentCoursePlanCoursePath(
  planId: string,
  courseId: string,
  courseStatementUnique = ''
): string {
  const params = new URLSearchParams();
  params.set('courseStatementUnique', courseStatementUnique);
  return `${getStudentCoursePlanDetailPath(planId)}/course/${encodeURIComponent(courseId)}?${params.toString()}`;
}

/**
 * Build the GET path for fetching a plan's cluster/pathway assignments.
 *
 * @param planId - The student course plan ID
 * @returns URL path for the clusters-pathways endpoint
 */
export function getStudentCoursePlanClustersPathwaysPath(planId: string): string {
  return `${getStudentCoursePlanDetailPath(planId)}/clusters-pathways`;
}

/**
 * POST path for validating prerequisite / co-requisite selections before adding to plan.
 */
export function getValidateCourseRequisitesPath(): string {
  return `${getStudentCoursePlansBasePath()}/validate-course-requisites`;
}
/**
 * Student host school list endpoint.
 * Embedded: `/highschool/naviance-district-schools` (FC proxy).
 * Local dev: `/highschools` (direct to course-planner-service).
 */
export function getStudentHighschoolsPath(): string {
  if (isDirectApiMode()) {
    return '/highschools';
  }
  return '/highschool/naviance-district-schools';
}

/**
 * Student-host endpoint for the authenticated student's current school.
 *
 * @returns Current-school path served by the student host
 */
export function getStudentCurrentHighschoolPath(): string {
  return '/highschool/me';
}

/**
 * Student-host plans-of-study by active school endpoint.
 * Embedded: `/course-planner-next/plans-of-study/findByActiveSchool?...`.
 * Local direct mode: district/highschool-scoped course-planner-service route.
 */
export function getStudentPlansOfStudyByActiveSchoolPath(activeSchoolId: string): string {
  const encodedSchoolId = encodeURIComponent(activeSchoolId);
  const pageSize = 10000;

  if (isDirectApiMode()) {
    const districtId = getDistrictId();
    const highschoolId = getStudentHighschoolId();
    return `/plans-of-study/district/${encodeURIComponent(districtId)}/highschool/${encodeURIComponent(highschoolId)}/findByActiveSchool?activeSchool=${encodedSchoolId}&pageSize=${pageSize}`;
  }

  return `/course-planner-next/plans-of-study/findByActiveSchool?activeSchool=${encodedSchoolId}&pageSize=${pageSize}`;
}

/**
 * Student-host plans-of-study by district or school list endpoint.
 * Embedded: `/course-planner-next/plans-of-study/findByDistrictOrSchools`.
 * Local direct mode: district/highschool-scoped course-planner-service route.
 */
export function getStudentPlansOfStudyByDistrictOrSchoolsPath(): string {
  if (isDirectApiMode()) {
    const districtId = getDistrictId();
    const highschoolId = getStudentHighschoolId();
    return `/plans-of-study/district/${encodeURIComponent(districtId)}/highschool/${encodeURIComponent(highschoolId)}/findByDistrictOrSchools`;
  }

  return '/course-planner-next/plans-of-study/findByDistrictOrSchools';
}
